// src/pages/nfl/NflCheatSheets.jsx
import React, { useEffect, useMemo, useState } from "react";
const SOURCE = "/data/nfl/classic/latest/projections.json";
const META_URL = "/data/nfl/classic/latest/meta.json";
const teamLogo = (abbr) =>
  abbr ? `/logos/nfl/${String(abbr).toUpperCase()}.png` : "";
/* =========================================================
   CONFIG
   ========================================================= */
const SCOPES = ["All", "QB", "RB", "WR", "TE", "DST"];
const METRICS = [
  {
    site: "DK",
    metric: "Proj",
    field: "dk_proj",
  },
  {
    site: "FD",
    metric: "Proj",
    field: "fd_proj",
  },
  {
    site: "DK",
    metric: "pOWN%",
    field: "dk_pown",
    percent: true,
  },
  {
    site: "FD",
    metric: "pOWN%",
    field: "fd_pown",
    percent: true,
  },
  {
    site: "DK",
    metric: "Val",
    field: "dk_val",
  },
  {
    site: "FD",
    metric: "Val",
    field: "fd_val",
  },
  {
    site: "DK",
    metric: "Floor",
    field: "dk_floor",
  },
  {
    site: "DK",
    metric: "Ceiling",
    field: "dk_ceiling",
  },
  {
    site: "FD",
    metric: "Floor",
    field: "fd_floor",
  },
  {
    site: "FD",
    metric: "Ceiling",
    field: "fd_ceiling",
  },
  {
    site: "DK",
    metric: "Opt%",
    field: "dk_opt",
    percent: true,
  },
  {
    site: "FD",
    metric: "Opt%",
    field: "fd_opt",
    percent: true,
  },
  {
    site: "DK",
    metric: "Lev%",
    field: "dk_lev",
    percent: true,
  },
  {
    site: "FD",
    metric: "Lev%",
    field: "fd_lev",
    percent: true,
  },
  {
    site: "DK",
    metric: "Rtg",
    field: "dk_rtg",
  },
  {
    site: "FD",
    metric: "Rtg",
    field: "fd_rtg",
  },
];
/* =========================================================
   FETCH PROJECTIONS
   ========================================================= */
function useProjections(url) {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, {
          cache: "no-store",
        });
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}`);
        }
        const j = await r.json();
        const arr = Array.isArray(j)
          ? j
          : Array.isArray(j?.players)
          ? j.players
          : Array.isArray(j?.projections)
          ? j.projections
          : [];
        if (alive) {
          setPlayers(arr);
        }
      } catch (e) {
        if (alive) {
          setErr(String(e));
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return {
    players,
    loading,
    err,
  };
}
/* =========================================================
   FETCH META
   ========================================================= */
function useMeta(url) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, {
          cache: "no-store",
        });
        if (!r.ok) {
          throw new Error();
        }
        const j = await r.json();
        if (alive) {
          setMeta(j);
        }
      } catch {
        if (alive) {
          setMeta(null);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return meta;
}
/* =========================================================
   HELPERS
   ========================================================= */
function toNumber(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }
  if (typeof value === "number") {
    return Number.isFinite(value)
      ? value
      : null;
  }
  const raw = String(value)
    .trim()
    .replace(/,/g, "")
    .replace(/%$/, "");
  if (!raw) {
    return null;
  }
  const n = Number(raw);
  return Number.isFinite(n)
    ? n
    : null;
}
function normalizePosition(pos) {
  const p = String(pos || "")
    .trim()
    .toUpperCase();
  if (
    p === "D" ||
    p === "DEF" ||
    p === "DST" ||
    p === "D/ST" ||
    p === "DEFENSE"
  ) {
    return "DST";
  }
  return p;
}
const fmt = {
  number(v) {
    if (
      v === null ||
      v === undefined ||
      v === ""
    ) {
      return "";
    }
    const s = String(v).trim();
    const n = Number(
      s.replace(/[, ]/g, "")
    );
    if (!Number.isFinite(n)) {
      return s;
    }
    return Number.isInteger(n)
      ? String(n)
      : String(n.toFixed(2))
          .replace(/0+$/, "")
          .replace(/\.$/, "");
  },
  percent(v) {
    if (
      v === null ||
      v === undefined ||
      v === ""
    ) {
      return "";
    }
    const raw = String(v).trim();
    if (!raw) {
      return "";
    }
    if (/%$/.test(raw)) {
      return raw.replace(/\s+/g, "");
    }
    const n = Number(
      raw.replace(/[, ]/g, "")
    );
    if (!Number.isFinite(n)) {
      return raw;
    }
    const pct =
      Math.abs(n) <= 1.01
        ? n * 100
        : n;
    const rounded1 =
      Math.round(pct * 10) / 10;
    const isIntish =
      Math.abs(
        rounded1 -
          Math.round(rounded1)
      ) < 1e-9;
    return (
      (isIntish
        ? String(Math.round(rounded1))
        : String(
            rounded1.toFixed(1)
          )) + "%"
    );
  },
};
/* =========================================================
   PAGE
   ========================================================= */
export default function NflCheatSheets() {
  const {
    players,
    loading,
    err,
  } = useProjections(SOURCE);
  const meta = useMeta(META_URL);
  const [scope, setScope] =
    useState("All");
  const [selMetric, setSelMetric] =
    useState("dk_proj");
  const [theme, setTheme] =
    useState("dark");
  const isDark =
    theme === "dark";
  /* =========================================================
     SELECTED METRIC
     ========================================================= */
  const selectedMetric = useMemo(
    () =>
      METRICS.find(
        (m) =>
          m.field === selMetric
      ) || METRICS[0],
    [selMetric]
  );
  /* =========================================================
     BUILD CURRENT TOP 10
     ========================================================= */
  const table = useMemo(() => {
    if (!selectedMetric) {
      return null;
    }
    const pool =
      scope === "All"
        ? players
        : players.filter(
            (player) =>
              normalizePosition(
                player?.pos
              ) === scope
          );
    const ranked = pool
      .map((player) => ({
        player,
        value: toNumber(
          player?.[
            selectedMetric.field
          ]
        ),
      }))
      .filter(
        (item) =>
          item.value !== null
      )
      .sort(
        (a, b) =>
          b.value - a.value
      )
      .slice(0, 10);
    return {
      label: `Top 10 ${scope} ${selectedMetric.site} ${selectedMetric.metric}`,
      metric:
        selectedMetric.metric,
      percent:
        selectedMetric.percent ||
        false,
      rows: ranked.map(
        ({ player, value }) => ({
          Player:
            player?.player ?? "",
          Team:
            player?.team ?? "",
          [selectedMetric.metric]:
            value,
        })
      ),
    };
  }, [
    players,
    scope,
    selectedMetric,
  ]);
  const rowCount =
    table?.rows?.length || 0;
  /* =========================================================
     LAST UPDATED
     ========================================================= */
  const updatedStamp = useMemo(() => {
    if (!meta) {
      return null;
    }
    const art =
      meta?.artifacts?.find((x) =>
        String(
          x?.path || ""
        ).endsWith(SOURCE)
      );
    const raw =
      art?.last_updated ||
      art?.tags?.last_updated ||
      meta?.last_updated;
    if (!raw) {
      return null;
    }
    const d =
      new Date(raw);
    if (isNaN(d.getTime())) {
      return null;
    }
    return d.toLocaleString(
      undefined,
      {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }
    );
  }, [meta]);
  /* =========================================================
     STYLING
     ========================================================= */
  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";
  const cardClass = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm";
  const headerBgClass = isDark
    ? "bg-slate-900 text-slate-100"
    : "bg-slate-50 text-slate-800";
  const rowOddClass = isDark
    ? "bg-slate-950"
    : "bg-white";
  const rowEvenClass = isDark
    ? "bg-slate-900/85"
    : "bg-slate-50/80";
  const tableTextClass = isDark
    ? "text-slate-100"
    : "text-slate-900";
  const cell =
    "px-3 py-2 text-left";
  const headerBase =
    "px-3 py-2 text-left font-semibold";
  const small =
    "text-[11.5px] md:text-[12.5px]";
  /* =========================================================
     RENDER
     ========================================================= */
  return (
    <div className={outerClass}>
      <div className="w-full px-4 py-5 md:px-6">
        {/* ===================================================
            HEADER
            =================================================== */}
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-xl font-bold tracking-tight">NFL — Cheat Sheets</h1>
            <span className={"text-xs font-normal italic " + (isDark ? "text-slate-100" : "text-slate-700")}>Updated: {updatedStamp ?? "—"}</span>
          </div>
          {/* THEME */}
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <div className={"inline-flex rounded-full border p-1 " + (isDark ? "border-slate-700/70 bg-slate-900/70" : "border-slate-300 bg-white")}>
              {["dark", "light"].map(
                (t) => {
                  const active =
                    theme === t;
                  return (
                    <button
                      key={t}
                      onClick={() =>
                        setTheme(t)
                      }
                      className={
                        "rounded-full px-2.5 py-1 text-[11px] transition sm:text-xs " +
                        (
                          active
                            ? "bg-sky-500 text-slate-950 shadow-sm"
                            : (isDark ? "text-slate-300 hover:text-white" : "text-slate-500 hover:text-slate-900")
                        )
                      }
                    >
                      {t === "dark"
                        ? "Dark"
                        : "Light"}
                    </button>
                  );
                }
              )}
            </div>
          </div>
        </div>
        {/* ===================================================
            CONTROLS
            =================================================== */}
        <div className={cardClass + " mb-4 px-4 py-3"}>
          {/* POSITION TABS */}
          <div className={"mb-3 flex flex-wrap gap-1 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
            {SCOPES.map((tab) => {
              const active =
                scope === tab;
              return (
                <button
                  key={tab}
                  onClick={() =>
                    setScope(tab)
                  }
                  className={
                    "h-9 border-b-2 px-3 text-xs font-semibold transition " +
                    (
                      active
                        ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                        : isDark
                        ? "border-transparent text-slate-400 hover:text-white"
                        : "border-transparent text-slate-500 hover:text-slate-900"
                    )
                  }
                >
                  {tab}
                </button>
              );
            })}
          </div>
          {/* METRIC SELECTOR */}
          <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm">
            <label htmlFor="cheat-metric" className={"text-xs font-medium " + (isDark ? "text-slate-400" : "text-slate-500")}>
              Metric
            </label>
            <select
              id="cheat-metric"
              className={
                "h-8 min-w-0 max-w-full rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                (
                  isDark
                    ? "border-slate-700 bg-slate-950/80 text-slate-100"
                    : "border-slate-300 bg-white text-slate-900"
                )
              }
              value={selMetric}
              onChange={(e) =>
                setSelMetric(
                  e.target.value
                )
              }
            >
              {METRICS.map(
                (metric) => (
                  <option
                    key={
                      metric.field
                    }
                    value={
                      metric.field
                    }
                  >
                    {metric.site}{" "}
                    {metric.metric}
                  </option>
                )
              )}
            </select>
          </div>
        </div>
        {/* ===================================================
            CARD
            =================================================== */}
        <div className={cardClass}>
          {loading ? (
            <div className="px-6 py-10 text-center text-sm text-slate-400">
              Loading…
            </div>
          ) : err ? (
            <div className="px-6 py-10 text-center text-sm text-red-400">
              Error: {err}
            </div>
          ) : !players.length ? (
            <div className="px-6 py-10 text-center text-sm text-slate-400">
              No projection data
              found. Make sure{" "}
              <code>
                {SOURCE}
              </code>{" "}
              exists.
            </div>
          ) : !table ? (
            <div className="px-6 py-10 text-center text-sm text-slate-400">
              No cheat sheet table
              available.
            </div>
          ) : (
            <>
              {/* =============================================
                  TABLE TITLE
                  ============================================= */}
              <div
                className={
                  headerBgClass +
                  " flex items-center justify-between rounded-t-2xl border-b border-slate-800/80 px-4 py-3"
                }
              >
                <div className="text-xs font-semibold md:text-sm">
                  {table.label}
                </div>
              </div>
              {/* =============================================
                  TABLE
                  ============================================= */}
              <div className="rounded-b-2xl">
                <table
                  className={`w-full table-fixed border-separate ${small} ${tableTextClass}`}
                  style={{
                    borderSpacing: 0,
                  }}
                >
                  <thead
                    className={
                      headerBgClass
                    }
                  >
                    <tr>
                      <th
                        className={
                          headerBase +
                          " break-words"
                        }
                      >
                        Player
                      </th>
                      <th
                        className={
                          headerBase +
                          " break-words"
                        }
                      >
                        Team
                      </th>
                      <th
                        className={
                          headerBase +
                          " break-words"
                        }
                      >
                        {
                          table.metric
                        }
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.length ? (
                      table.rows.map(
                        (row, i) => {
                          const rowBg =
                            i % 2 === 0
                              ? rowOddClass
                              : rowEvenClass;
                          return (
                            <tr
                              key={`${row.Player}-${i}`}
                              className={
                                rowBg
                              }
                            >
                              {/* PLAYER */}
                              <td
                                className={
                                  cell +
                                  " break-words border-t border-slate-800/40 font-medium"
                                }
                              >
                                {
                                  row.Player
                                }
                              </td>
                              {/* TEAM */}
                              <td
                                className={
                                  cell +
                                  " break-words border-t border-slate-800/40"
                                }
                              >
                                <div className="flex items-center gap-1.5">
                                  {row.Team ? (
                                    <img
                                      src={teamLogo(
                                        row.Team
                                      )}
                                      alt=""
                                      className="h-4 w-4 shrink-0 object-contain opacity-90"
                                    />
                                  ) : null}
                                  <span>
                                    {
                                      row.Team
                                    }
                                  </span>
                                </div>
                              </td>
                              {/* VALUE */}
                              <td
                                className={
                                  cell +
                                  " break-words border-t border-slate-800/40"
                                }
                              >
                                {table.percent
                                  ? fmt.percent(
                                      row[
                                        table
                                          .metric
                                      ]
                                    )
                                  : fmt.number(
                                      row[
                                        table
                                          .metric
                                      ]
                                    )}
                              </td>
                            </tr>
                          );
                        }
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-4 py-10 text-center text-slate-500"
                        >
                          No data
                          available for{" "}
                          {
                            table.label
                          }
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}